<script lang="ts">
  import { onMount } from "svelte"
  import type { ClassValue, HTMLAttributes } from "svelte/elements"
  import { scrollIntoView } from "./scroll-into-view"

  const DEFAULT_FRAME_PADDING = 12

  let {
    images,
    active = $bindable(0),
    fit = "cover",
    isMirrored,
    isFramed,
    framePadding,
    imageClasses,
    ...props
  }: {
    images: HTMLImageElement["src"][]
    active?: number
    imageClasses?: ClassValue
    isFramed?: boolean
    isMirrored?: boolean
    framePadding?: { top?: number; right?: number; bottom?: number; left?: number }
    fit?: "cover" | "contain"
  } & HTMLAttributes<HTMLDivElement> = $props()

  let container = $state<HTMLElement>()
  const observer = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries)
        if (isIntersecting)
          active = prevActive = Array.prototype.indexOf.call(container?.children, target)
    },
    { threshold: 0.5 },
  )

  const observeIntersection = () => {
    for (const child of container?.children ?? []) observer.observe(child)
  }

  let prevActive = active

  onMount(() => {
    observeIntersection()
    return () => observer?.disconnect()
  })

  $effect(() => {
    if (prevActive === active) return

    observer.disconnect()
    scrollIntoView(container?.children[active]).then(observeIntersection)
    prevActive = active
  })
</script>

<div
  {...props}
  class={[
    "root isolate flex size-full snap-x snap-mandatory snap-always gap-3 overflow-y-auto *:snap-center",
    { framed: isFramed },
  ]}
  style:--frame-padding-top="{isFramed ? (framePadding?.top ?? DEFAULT_FRAME_PADDING) : 0}px"
  style:--frame-padding-right="{isFramed ? (framePadding?.right ?? DEFAULT_FRAME_PADDING) : 0}px"
  style:--frame-padding-bottom="{isFramed ? (framePadding?.bottom ?? DEFAULT_FRAME_PADDING) : 0}px"
  style:--frame-padding-left="{isFramed ? (framePadding?.left ?? DEFAULT_FRAME_PADDING) : 0}px"
  bind:this={container}
>
  {#each images as src}
    <div class="image-container size-full shrink-0">
      <figure
        class={["relative size-full overflow-hidden", { "rounded-3xl": isFramed }, imageClasses]}
      >
        <img
          alt=""
          {src}
          class={[
            "absolute inset-0 size-full max-h-full max-w-full",
            fit === "cover" ? "object-cover" : "m-auto object-contain",
            { "-scale-x-100": isMirrored },
          ]}
        />
      </figure>
    </div>
  {/each}
</div>

<style lang="postcss">
  .root {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      @apply hidden;
    }
  }

  .image-container {
    padding: var(--frame-padding-top) var(--frame-padding-right) var(--frame-padding-bottom)
      var(--frame-padding-left);
  }

  .framed {
    .image-container:first-child {
      &,
      figure {
        animation-duration: 300ms;
        animation-delay: 500ms;
        animation-fill-mode: forwards;
      }

      @apply p-0;

      animation-name: frame;

      @keyframes frame {
        to {
          padding: var(--frame-padding-top) var(--frame-padding-right) var(--frame-padding-bottom)
            var(--frame-padding-left);
        }
      }

      figure {
        @apply rounded-none;

        animation-name: frame-figure;

        @keyframes frame-figure {
          to {
            @apply rounded-3xl;
          }
        }
      }
    }
  }

  @supports (animation-timeline: scroll(inline)) {
    .image-container:nth-child(2) {
      z-index: -2;

      figure {
        animation: 1ms swipe2 linear forwards;
        animation-timeline: scroll(inline);

        @keyframes swipe2 {
          0% {
            transform: translateX(-100%) scale(0.85);
            opacity: 0.3;
          }
          50% {
            transform: translateX(0%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateX(0%);
          }
        }
      }
    }

    .image-container:nth-child(3) {
      z-index: -3;

      figure {
        animation: 1ms swipe3 linear forwards;
        animation-timeline: scroll(inline);

        @keyframes swipe3 {
          0%,
          50% {
            transform: translateX(-100%) scale(0.85);
            opacity: 0;
          }
          50.001% {
            transform: translateX(-100%) scale(0.85);
            opacity: 0.3;
          }
          100% {
            transform: translateX(0%) scale(1);
            opacity: 1;
          }
        }
      }
    }
  }
</style>
